import { useCallback, useEffect, useRef, useState } from "react";
import { StyleSheet, Text } from "react-native";
import { randomUUID } from "expo-crypto";
import { CHECKOUT_WORDS, money } from "@libs/checkout-core";
import { musicPriceMinor } from "@libs/shared-ui";
import type { MusicAccessDetailsModel } from "@libs/shared-ui";
import type { EngineCheckoutSession } from "@components/Checkout/engineSession";
import {
  PurchaseSheet,
  purchaseSelection,
} from "@components/Checkout/PurchaseSheet";
import { usePurchaseFunnel } from "@components/Checkout/purchaseFunnel";
import type { PurchaseFlow } from "@components/Checkout/PurchaseSheet";
import { CheckoutShape } from "@components/Checkout/native/CheckoutShape";
import { colors } from "@components/Checkout/native/colors";
import { OrderSummaryView } from "@components/Checkout/native/OrderSummaryCard";
import {
  BACK_TO_MUSIC,
  MUSIC_UNLOCK_EXPIRED,
  musicCheckoutSession,
  musicPurchasePlatform,
  musicUnlockRefusal,
  type MusicOwnership,
  type MusicUnlockPorts,
} from "./musicUnlock";

/** Why the music could not be had, and whether another try could help. */
type Refusal = { message: string; retry: boolean };

/** The unlock's checkout, and the account it was opened for (the ports as
 *  they were): it is that account's only. */
type MusicCheckout = {
  session: EngineCheckoutSession;
  asked: MusicUnlockPorts;
};

/**
 * Unlocking music (LIS-1038): the purchase flow every sale in the app runs
 * (PurchaseSheet), for one item. What is bought is the song, summed up on
 * one line — its artwork, title, artist and price — with nothing to
 * choose; then the fan's sign-in, as for tickets; then Continue.
 *
 * Free music (no price, or a valid unlock code) is completed by the
 * server: it is the fan's at once, and the sheet ends on "It's yours".
 * Music with a price opens the tickets' checkout for the one item — native
 * on iOS and Android, the embedded web checkout on app-web. The moment it
 * succeeds the music is granted from the engine's order and shown
 * unlocked; Done, or the sheet going any other way after that, hands
 * Details over as after any purchase. Back, or the hold running out,
 * returns to the music (the checkout gives the hold back). Music the fan
 * owns already is shown unlocked, and the sheet closes.
 */
export function MusicUnlockSheet({
  model,
  dataSharingConsent,
  ports,
  close,
}: {
  model: MusicAccessDetailsModel;
  dataSharingConsent: boolean;
  ports: MusicUnlockPorts;
  close: () => void;
}) {
  const { accessId, unlockCode } = model;
  const priceMinor = musicPriceMinor(model.effectivePrice, model.priceCurrency);
  const free = priceMinor <= 0;
  const total = free ? "Free" : money(priceMinor, model.priceCurrency);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  // The Details' purchase funnel (LIS-1110): the unlock carries its ID.
  const funnel = usePurchaseFunnel();

  // The fan has it, or has paid for it (on the account `theirs` names):
  // Details takes over once, when the sheet goes. Gone already when the
  // answer comes late: at once.
  const theirs = useRef<MusicUnlockPorts | null>(null);
  const handedOver = useRef(false);
  const gone = useRef(false);
  const handOver = useCallback(() => {
    const owner = theirs.current;
    if (!owner || handedOver.current || !owner.isCurrentUser()) return;
    handedOver.current = true;
    owner.complete(accessId);
  }, [accessId]);
  useEffect(() => {
    gone.current = false;
    return () => {
      gone.current = true;
      handOver();
    };
  }, [handOver]);
  const finish = useCallback(() => {
    handOver();
    close();
  }, [handOver, close]);

  /** Shown unlocked, for the account the answer came to (`asked`). */
  const unlocked = useCallback(
    (asked: MusicUnlockPorts) => {
      if (!asked.isCurrentUser()) return;
      asked.markUnlocked(accessId);
      if (unlockCode) asked.clearUnlockCode();
      theirs.current = asked;
      if (gone.current) handOver();
    },
    [accessId, unlockCode, handOver],
  );

  // The engine's word on the order, asked once: granted is shown at once;
  // anything else is the webhook's to settle, and the accesses are read
  // again.
  const confirming = useRef(false);
  const confirm = ({ session, asked }: MusicCheckout) => {
    if (confirming.current || !asked.isCurrentUser()) return;
    confirming.current = true;
    asked
      .confirm({ accessId, clientSecret: session.clientSecret })
      .then(({ status }: { status: MusicOwnership }) => {
        if (!asked.isCurrentUser()) return;
        if (status === "granted") unlocked(asked);
        else asked.refresh();
      })
      .catch(() => {
        if (asked.isCurrentUser()) asked.refresh();
      });
  };

  // What is bought, on one line: the order card, as the checkout and the
  // end draw it.
  const item = (
    <OrderSummaryView
      artwork={model.heroImageUrl}
      title={model.title}
      detail={model.artistName}
      fee={null}
      total={total}
    />
  );

  const { Checkout } = ports;
  const flow: PurchaseFlow<MusicCheckout> = {
    testID: "music-unlock-sheet",
    goods: "items",
    keyboard: "sheet",
    buyer: ports.buyer,
    onFunnelStep: funnel?.step,
    choice: {
      ready: true,
      // The one song, at its price.
      selection: purchaseSelection(1, priceMinor, model.priceCurrency),
      words: free ? "Continue" : `Continue · ${total}`,
      // Refused for good (an unlock code that does not work, music no
      // longer on sale): nothing to continue with.
      disabled: refusal?.retry === false,
      render: () => (
        <>
          {item}
          {refusal ? (
            <Text accessibilityRole="alert" style={styles.message}>
              {refusal.message}
            </Text>
          ) : null}
        </>
      ),
    },
    checkout: {
      open: async () => {
        // The answers belong to the account that asked.
        const asked = ports;
        if (!asked.isCurrentUser()) return { kind: "close" };
        setRefusal(null);
        const requestedAt = Date.now();
        const idempotencyKey = randomUUID();
        try {
          const result = await asked.create({
            accessId,
            unlockCode,
            dataSharingConsent,
            purchasePlatform: musicPurchasePlatform(),
            idempotencyKey,
            ...(funnel ? { funnelId: funnel.id } : {}),
          });
          if (result.kind === "engine") {
            if (!asked.isCurrentUser()) {
              await asked
                .release({ accessId, clientSecret: result.clientSecret })
                .catch(() => undefined);
              return { kind: "close" };
            }
            return {
              kind: "checkout",
              checkout: {
                session: musicCheckoutSession(accessId, result, requestedAt),
                asked,
              },
            };
          }
          if (!asked.isCurrentUser()) return { kind: "close" };
          unlocked(asked);
          // Free, or a valid code: the server completed the order. Already
          // theirs: nothing was sold.
          return result.kind === "unlocked"
            ? { kind: "complete", order: item }
            : { kind: "close" };
        } catch (error) {
          setRefusal(musicUnlockRefusal(error));
          return { kind: "back" };
        }
      },
      key: ({ session }) => session.clientSecret,
      // Another account signing in drops it, back to the music.
      owner: ({ asked }) => asked.buyer.user,
      shape: (_session, { email }) => (
        <CheckoutShape
          email={email}
          free={free}
          backLabel={BACK_TO_MUSIC}
          destination={CHECKOUT_WORDS.items.destination}
        />
      ),
      render: (held, host) => (
        <Checkout
          session={held.session}
          backLabel={BACK_TO_MUSIC}
          goods="items"
          // Back to the music (the checkout gives the hold back); the hold
          // ran out: say so, and Continue holds another.
          onBack={(note) => {
            host.back();
            setRefusal(
              note ? { message: MUSIC_UNLOCK_EXPIRED, retry: true } : null,
            );
          }}
          onSucceeded={() => {
            host.succeeded();
            theirs.current = held.asked;
            confirm(held);
          }}
          onClose={() => {
            // Back from the hosted checkout, or done: whether it was paid
            // there is the engine's to say.
            if (!theirs.current) confirm(held);
            host.close();
          }}
          onChrome={host.chrome}
        />
      ),
      // The checkout gives its hold back when it goes; one that came after
      // the sheet had gone never mounted to.
      release: ({ session, asked }) => {
        asked
          .release({ accessId, clientSecret: session.clientSecret })
          .catch(() => undefined);
      },
    },
  };
  return <PurchaseSheet flow={flow} close={finish} />;
}

const styles = StyleSheet.create({
  // As the tickets say what happened, under them.
  message: { color: colors.secondary, fontSize: 13, textAlign: "center" },
});
